import ApiService from '@/common/api.service'
import * as utils from '@/common/utils'
import * as fleetMarkers from '@/maps/google/fleetMarkers'
import * as googleMaps from '@/common/googleMaps'
import * as assetMarkers from '@/maps/google/assetMarkers'
import * as fleetWidgetHelpers from '@/store/monitoring/fleetWidget.helpers'

const buildVehIdentification = (vehicle, options) => {
  let stateIcon
  if (vehicle.comms_status_code === 'offline') {
    stateIcon = assetMarkers.labelStates.offline
  } else if (vehicle.active_trip) {
    stateIcon = assetMarkers.labelStates.moving
  } else if (vehicle.local_time) {
    stateIcon = assetMarkers.labelStates.stopped
  } else {
    stateIcon = assetMarkers.labelStates.offline
  }

  let labelEntries = []
  if (options.sortBy === 'reg_no') {
    if (options.showRegNo) labelEntries.push(vehicle.reg_no)
    if (options.showFleetNo && vehicle.fleet_no) labelEntries.push(vehicle.fleet_no)
    if (labelEntries.length === 0) labelEntries.push(vehicle.reg_no)
  } else {
    if (options.showFleetNo && vehicle.fleet_no) labelEntries.push(vehicle.fleet_no)
    if (options.showRegNo) labelEntries.push(vehicle.reg_no)
    if (labelEntries.length === 0 && vehicle.fleet_no) {
      labelEntries.push(vehicle.fleet_no)
    } else if (labelEntries.length === 0 && !vehicle.fleet_no) {
      labelEntries.push(vehicle.reg_no)
    }
  }

  let labelDivs
  if (labelEntries.length === 1) {
    labelDivs = [`<div>${stateIcon} ${labelEntries[0]}</div>`]
  } else {
    labelDivs = [`<div class="border-bottom mb-1">${stateIcon} ${labelEntries[0]}</div>`]
  }

  // Now add the rest of the labelDivs
  for (let i = 1; i < labelEntries.length; i++) {
    labelDivs.push(`<div>${labelEntries[i]}</div>`)
  }

  return labelDivs.join('')
}

export const state = {
  prevClientId: null,
  queryByClient: null,
  vehicles: [],
  vehicleGroups: [],
  selectedVehicleId: null,
  fleetOptionsOpen: false,
  options: {
    showParkedVehicles: true,
    showActiveVehicles: true,
    showOfflineVehicles: true,
    showNoTelemVehicles: true,
    showRegNo: true,
    showFleetNo: true,
    showVehMakeModel: true,
    showDriver: true,
    sortBy: 'reg_no', // 'reg_no', 'fleet_no'
    groupId: null
  }
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @param state Object
   *
   * @return Object
   */
  get (state) {
    return state
  },

  /**
   * Returns either a client object or null depending on whether
   * queries should be made by client.
   *
   * @param state
   *
   * @return {Object|null}
   */
  getClient (state) {
    return state.queryByClient
  },

  getVehicles (state) {
    const options = state.options
    return state.vehicles.filter((vehicle) => {
      if (vehicle.comms_status_code === 'offline') return options.showOfflineVehicles
      if (vehicle.active_trip) return options.showActiveVehicles
      if (vehicle.local_time) return options.showParkedVehicles
      if (!vehicle.local_time) return options.showNoTelemVehicles

      return false
    })
  },

  getSelectedVehicleId (state) {
    return state.selectedVehicleId
  },

  getFleetOptionsOpen (state) {
    return state.fleetOptionsOpen
  }
}

export const actions = {
  /**
   * Query/find one or vehicles and their status data.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    const queryByClient = context.getters['getClient']
    const options = context.getters['get'].options
    const additionalParams = { 'unit_assigned_as': 'primary', ...params, order_by: options.sortBy }

    let url
    if (options.groupId !== null) {
      url = `vehicles/status/trip/by-vehicle-group/${options.groupId}`
    } else {
      url = `vehicles/status/trip/by-client/${queryByClient.id}`
    }

    if (queryByClient !== null) {
      return ApiService
        .get(url, additionalParams)
        .then((response) => {
          context.commit('setVehicles', response.data.data)
          return response
        })
    } else {
      return new Promise((resolve) => {
        resolve()
      })
    }
  },

  /**
   * Query/find all vehicle groups for the current client.
   *
   * @param context Object
   *
   * @return Promise
   */
  queryVehicleGroups (context) {
    const queryByClient = context.getters['getClient']
    const prevClientId = context.getters['get'].prevClientId

    if (queryByClient !== null && (prevClientId !== queryByClient.id || prevClientId === null)) {
      return ApiService
        .get(`vehicles/groups/listing/by-client/${queryByClient.id}`)
        .then((response) => {
          context.commit('setPrevClientId', queryByClient.id)
          context.commit('setVehicleGroups', response.data.data)
          return response
        })
    } else {
      return new Promise((resolve) => {
        resolve()
      })
    }
  }
}

export const mutations = {
  /**
   * Purge the module's state.
   *
   * @param state Object
   */
  purge (state) {
    state.queryByClient = null
    state.prevClientId = null
    state.vehicles = []
    state.vehicleGroups = []
    state.options.groupId = null
    fleetMarkers.clearFleetMarkers()
  },

  /**
   * Sets the client for which the POI's should be queried.
   *
   * @param state
   * @param client Object|null
   */
  setClient (state, client) {
    state.queryByClient = client
    state.vehicles = []
    state.vehicleGroups = []
    state.options.groupId = null
    fleetMarkers.clearFleetMarkers()
  },

  setPrevClientId (state, clientId) {
    state.prevClientId = clientId
  },

  setVehicles (state, vehicles) {
    state.vehicles = vehicles
    const options = state.options
    const latestVehicleIds = new Map()

    vehicles.forEach((vehicle) => {
      latestVehicleIds.set(vehicle.id, true)
      fleetMarkers.setFleetMarker(
        googleMaps.map, vehicle.id, vehicle, buildVehIdentification(vehicle, options), { offsetY: -10 })
    })

    fleetMarkers.removeDetachedMarkers(latestVehicleIds)
    fleetMarkers.updateMarkerCluster()
  },

  setVehicleGroups (state, groups) {
    state.vehicleGroups = groups
  },

  setSelectedVehicleId (state, vehicleId) {
    state.selectedVehicleId = vehicleId
    const vehicle = state.vehicles.find(vehicle => vehicle.id === vehicleId)
    if (vehicle) {
      googleMaps.panToLatLng(googleMaps.map, vehicle.latitude, vehicle.longitude)
    }
  },

  setFleetOptionsOpen (state, open) {
    state.fleetOptionsOpen = open

    if (open) {
      fleetWidgetHelpers.queryVehicleGroups()
    }
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    utils.mutateProp(state, name, value)
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/monitoring/fleetWidgetModule.js